import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';
import { moduleAPI } from '../services/api.ts';

interface PermissionModalProps {
  employeeId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const PermissionModal: React.FC<PermissionModalProps> = ({
  employeeId,
  onClose,
  onSuccess,
}) => {
  const [modulePermissions, setModulePermissions] = useState<any[]>([]);
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const permissionsResponse = await moduleAPI.getEmployeePermissions(
          employeeId
        );

        if (!permissionsResponse.success) {
          throw new Error(
            permissionsResponse.message ||
              'Failed to fetch employee permissions'
          );
        }

        setModulePermissions(permissionsResponse.data.permissions);
        setEmployeeData(permissionsResponse.data.employee);
      } catch (error: any) {
        setError(error.message || 'Failed to fetch permissions');
        console.error('Error in fetchData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [employeeId]);

  const handlePermissionChange = (moduleIndex: number, permission: string) => {
    setModulePermissions((prev) => {
      const updated = [...prev];
      const module = { ...updated[moduleIndex] };

      const currentPermissions = module.granted_permissions || [];
      module.granted_permissions = currentPermissions.includes(permission)
        ? currentPermissions.filter((p: any) => p !== permission)
        : [...currentPermissions, permission];

      updated[moduleIndex] = module;
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      await moduleAPI.updateEmployeePermissions(employeeId, modulePermissions);
      onSuccess();
    } catch (error: any) {
      setError(error.message || 'Failed to update permissions');
      console.error('Error updating permissions:', error);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className="inline-block transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 sm:align-middle max-h-[85vh]">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div
            className="sm:flex sm:items-start overflow-y-auto"
            style={{ maxHeight: 'calc(85vh - 100px)' }}
          >
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <div className="border-b pb-4 mb-4">
                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  Manage Permissions
                </h3>
                {employeeData && (
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>
                      <span className="font-medium">Name:</span>{' '}
                      {employeeData.name}
                    </p>
                  </div>
                )}
              </div>

              {error && (
                <div className="mt-2 rounded-md bg-red-50 dark:bg-red-900/30 p-4">
                  <div className="text-sm text-red-700 dark:text-red-300">
                    {error}
                  </div>
                </div>
              )}

              <div className="mt-4 space-y-8">
                {/* Module Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b dark:border-gray-700 pb-2">
                    Module Section
                  </h3>
                  <div className="space-y-4">
                    {modulePermissions
                      .filter(
                        (module) =>
                          !module.module_name.toLowerCase().includes('report')
                      )
                      .map((module, index) => {
                        // Find the original index in the full array to update correctly
                        const originalIndex = modulePermissions.findIndex(
                          (m) => m.module_id === module.module_id
                        );
                        const permissions = [
                          ...module.available_permissions,
                        ].sort((a, b) => {
                          const aGranted =
                            module.granted_permissions.includes(a);
                          const bGranted =
                            module.granted_permissions.includes(b);
                          if (aGranted && !bGranted) return -1;
                          if (!aGranted && bGranted) return 1;
                          return 0;
                        });
                        const totalPerms = permissions.length;
                        const perColumn = Math.ceil(totalPerms / 3);

                        return (
                          <div
                            key={module.module_id}
                            className="border dark:border-gray-700 rounded-md p-4 bg-gray-50 dark:bg-gray-700/30"
                          >
                            <h4 className="font-medium mb-3 text-gray-900 dark:text-white">
                              {module.module_name}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                              {[0, 1].map((colIndex) => (
                                <div key={colIndex} className="space-y-3">
                                  {permissions
                                    .slice(
                                      colIndex * perColumn,
                                      (colIndex + 1) * perColumn
                                    )
                                    .map((permission: string) => (
                                      <div
                                        key={permission}
                                        className="flex items-center justify-between"
                                      >
                                        <Switch
                                          checked={module.granted_permissions.includes(
                                            permission
                                          )}
                                          onChange={() =>
                                            handlePermissionChange(
                                              originalIndex,
                                              permission
                                            )
                                          }
                                          className={`${
                                            module.granted_permissions.includes(
                                              permission
                                            )
                                              ? 'bg-primary'
                                              : 'bg-gray-200 dark:bg-gray-600'
                                          } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                        >
                                          <span
                                            className={`${
                                              module.granted_permissions.includes(
                                                permission
                                              )
                                                ? 'translate-x-4'
                                                : 'translate-x-1'
                                            } inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`}
                                          />
                                        </Switch>
                                        <span className="text-sm text-gray-700 dark:text-gray-300 ml-3">
                                          {permission.charAt(0).toUpperCase() +
                                            permission.slice(1)}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Reports Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b dark:border-gray-700 pb-2">
                    Reports Section
                  </h3>
                  <div className="space-y-4">
                    {modulePermissions
                      .filter((module) =>
                        module.module_name.toLowerCase().includes('report')
                      )
                      .map((module, index) => {
                        // Find the original index in the full array to update correctly
                        const originalIndex = modulePermissions.findIndex(
                          (m) => m.module_id === module.module_id
                        );
                        const permissions = [
                          ...module.available_permissions,
                        ].sort((a, b) => {
                          const aGranted =
                            module.granted_permissions.includes(a);
                          const bGranted =
                            module.granted_permissions.includes(b);
                          if (aGranted && !bGranted) return -1;
                          if (!aGranted && bGranted) return 1;
                          return 0;
                        });
                        const totalPerms = permissions.length;
                        const perColumn = Math.ceil(totalPerms / 3);

                        return (
                          <div
                            key={module.module_id}
                            className="border dark:border-gray-700 rounded-md p-4 bg-gray-50 dark:bg-gray-700/30"
                          >
                            <h4 className="font-medium mb-3 text-gray-900 dark:text-white">
                              {module.module_name}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {[0, 1, 2].map((colIndex) => (
                                <div key={colIndex} className="space-y-3">
                                  {permissions
                                    .slice(
                                      colIndex * perColumn,
                                      (colIndex + 1) * perColumn
                                    )
                                    .map((permission: string) => (
                                      <div
                                        key={permission}
                                        className="flex items-center justify-between"
                                      >
                                        <Switch
                                          checked={module.granted_permissions.includes(
                                            permission
                                          )}
                                          onChange={() =>
                                            handlePermissionChange(
                                              originalIndex,
                                              permission
                                            )
                                          }
                                          className={`${
                                            module.granted_permissions.includes(
                                              permission
                                            )
                                              ? 'bg-primary'
                                              : 'bg-gray-200 dark:bg-gray-600'
                                          } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                        >
                                          <span
                                            className={`${
                                              module.granted_permissions.includes(
                                                permission
                                              )
                                                ? 'translate-x-4'
                                                : 'translate-x-1'
                                            } inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`}
                                          />
                                        </Switch>
                                        <span className="text-sm text-gray-700 dark:text-gray-300 ml-3">
                                          {permission.charAt(0).toUpperCase() +
                                            permission.slice(1)}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
